import { UseFetchResult } from '@api/common';
import { EmptyState } from '@common-components';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';
import Box from '@mui/material/Box';
import { lighten, useTheme } from '@mui/material/styles';
import { typedMemo, useStateObj } from '@utils';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useBreadcrumbSegment } from '../breadcrumb/breadcrumbHooks';
import styles from './MasterDetail.module.scss';
import {
  MasterDetailContext,
  MasterDetailContextProps,
} from './MasterDetailContext';

interface MasterDetailProps<T = unknown> {
  masterComponent: React.ReactNode;
  detailComponent: React.ReactNode;
  initialMasterSize?: number;
  minMasterSize?: number;
  maxMasterSize?: number;
  breadcrumbTitle?: string;
  readDetailHook?: (id: string | undefined) => UseFetchResult<T>;
  idFieldName?: keyof T;
}

export const MasterDetail = typedMemo(
  <T,>({
    masterComponent: MasterComponent,
    detailComponent: DetailComponent,
    initialMasterSize = 15,
    minMasterSize = 15,
    maxMasterSize = 50,
    breadcrumbTitle,
    readDetailHook,
    idFieldName = 'id' as keyof T,
  }: Readonly<MasterDetailProps<T>>) => {
    const theme = useTheme();
    const selectedId = useStateObj<string | undefined>();
    const selectedItem = useStateObj<T | undefined>();
    const items = useStateObj<T[]>([]);
    const loading = useStateObj(false);
    const detailLoading = useStateObj(false);
    const error = useStateObj<string | null>(null);
    const masterMinimized = useStateObj(false);

    // Get selected item ID for detail loading
    const selectedItemId = selectedId.value?.toString();

    // Use detail hook if provided
    const detailResult = readDetailHook ? readDetailHook(selectedItemId) : null;

    // Update detailLoading state
    useEffect(() => {
      detailLoading.set(detailResult?.loading ?? false);
    }, [detailResult?.loading, detailLoading]);

    // Update selectedItem with detailed data when loaded
    useEffect(() => {
      if (detailResult?.result && !detailResult.loading) {
        selectedItem.set(detailResult.result);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detailResult?.result, detailResult?.loading]);

    // Register this view with the breadcrumb system
    useBreadcrumbSegment(
      'master-detail',
      breadcrumbTitle || 'Liste',
      10, // order
    );

    const updateItem = useCallback(
      (updatedItem: T, idKey: keyof T = idFieldName) => {
        const currentItems = items.value;
        const index = currentItems.findIndex(
          (item) => item[idKey] === updatedItem[idKey],
        );
        if (index !== -1) {
          const newItems = [...currentItems];
          newItems[index] = updatedItem;
          items.set(newItems);
        }

        // Update selectedItem if this is the currently selected item
        if (
          selectedId.value !== undefined &&
          updatedItem[idKey]?.toString() === selectedId.value.toString()
        ) {
          selectedItem.set(updatedItem);
        }
      },
      [selectedId, selectedItem, items, idFieldName],
    );

    const deleteItem = useCallback(
      (itemId: string | number, idKey: keyof T = idFieldName) => {
        const filteredItems = items.value.filter(
          (item) => item[idKey] !== itemId,
        );
        items.set(filteredItems);

        if (selectedId.value?.toString() === itemId.toString()) {
          selectedId.set(undefined);
          selectedItem.set(undefined);
        }
      },
      [selectedId, selectedItem, items, idFieldName],
    );

    const toggleMasterMinimized = useCallback(() => {
      masterMinimized.set(!masterMinimized.value);
    }, [masterMinimized]);

    const refetch = useCallback(() => {
      // Empty implementation - ListView will handle data fetching
    }, []);

    const contextValue = useMemo<MasterDetailContextProps<T>>(
      () => ({
        items,
        selectedId,
        selectedItem,
        updateItem,
        deleteItem,
        loading,
        detailLoading,
        error,
        refetch,
        masterMinimized,
        toggleMasterMinimized,
      }),
      [
        items,
        selectedId,
        selectedItem,
        updateItem,
        deleteItem,
        loading,
        detailLoading,
        error,
        refetch,
        masterMinimized,
        toggleMasterMinimized,
      ],
    );
    if (error.value) {
      return (
        <EmptyState
          icon={ErrorOutline}
          textKey="error.loadingData"
          color="error"
          actionButton={{ labelKey: 'common.retry', onClick: refetch }}
        />
      );
    }

    return (
      <MasterDetailContext.Provider value={contextValue}>
        <Box
          className={styles.masterDetailContainer}
          sx={{
            [`& .${styles.resizeHandle}`]: {
              '&:hover, &[data-resize-handle-state="hover"]': {
                backgroundColor: `${theme.palette.primary.light} !important`,
                '& svg': {
                  color: `${theme.palette.primary.contrastText} !important`,
                },
              },
              '&[data-resize-handle-state="drag"]': {
                backgroundColor: `${lighten(theme.palette.primary.main, 0.5)} !important`,
                '& svg': {
                  color: `${theme.palette.primary.contrastText} !important`,
                },
              },
            },
          }}
        >
          <PanelGroup direction="horizontal">
            <Panel
              defaultSize={masterMinimized.value ? 0 : initialMasterSize}
              minSize={masterMinimized.value ? 0 : minMasterSize}
              maxSize={masterMinimized.value ? 0 : maxMasterSize}
              order={1}
              className={styles.masterPanel}
              style={{
                flexBasis: masterMinimized.value
                  ? '0%'
                  : `${initialMasterSize}%`,
                backgroundColor: theme.palette.background.default,
                borderRight: `1px solid ${theme.palette.background.default}`,
              }}
            >
              {MasterComponent}
            </Panel>

            {!masterMinimized.value && (
              <PanelResizeHandle className={styles.resizeHandle}>
                <MoreVertOutlined
                  sx={{
                    fontSize: '16px',
                    color: 'text.disabled',
                    transition: 'color 0.2s ease',
                  }}
                />
              </PanelResizeHandle>
            )}

            <Panel
              order={2}
              className={styles.detailPanel}
              style={{ backgroundColor: theme.palette.background.default }}
            >
              {!loading.value && DetailComponent}
            </Panel>
          </PanelGroup>
        </Box>
      </MasterDetailContext.Provider>
    );
  },
);
